import React from 'react';
import Image from 'next/future/image';
import { Disclosure } from '@headlessui/react';
import cn from 'classnames';
import dayjs from 'dayjs';
import { CtaLink } from '@/components/Cta/Cta';
import chevDown from '@/public/icons/smartForm/chevDown.svg';
import openLink from '@/public/icons/smartForm/openLink.svg';
import { formatDayJsDateShort } from '@/components/track-your-order/utils';
import { Shipment } from '@/lib/smartForm/types';
import settings from '@/settings';

const TrackingLine = ({
  isLatest,
  date,
  message,
  location,
}: {
  isLatest: boolean;
  date: string;
  message: string;
  location: string;
}) => (
  <div className="mb-4 flex flex-wrap border-b border-gray-200 pb-4">
    <div className="hidden w-[20%] sm:block">
      <p className="w-full">{isLatest && 'Latest update'}</p>
    </div>
    <div className="hidden w-[30%] flex-wrap sm:block">
      <div className="pl-0 sm:pl-2">
        <div className="w-full">{date}</div>
      </div>
    </div>
    <div className="w-[40%] flex-wrap sm:hidden">
      <div className="w-full break-words">{date}</div>
    </div>
    <div className="hidden w-[50%] flex-wrap sm:block">
      <div className="overflow-x-auto pl-0 sm:pl-2">
        <div className="w-full">
          {message} | {location}
        </div>
      </div>
    </div>
    <div className="flex w-[60%] flex-wrap overflow-x-auto sm:hidden">
      <div className="w-full">{message}</div>
      <div className="w-full">{location}</div>
    </div>
  </div>
);

function generateDispatchDays(shipment: Shipment, shipmentStatus: string) {
  const lineItem = shipment.lineItems[0];
  const minDispatch = lineItem.properties.find(
    (property) => property.name === '_dispatch_min',
  )?.value;
  const maxDispatch = lineItem.properties.find(
    (property) => property.name === '_dispatch_max',
  )?.value;
  const currentDate = dayjs();
  const startDay = dayjs(
    shipment.lineItems[0].properties.filter(
      (prop) => prop.name === '_add_cart_date_time',
    )[0]?.value,
  );
  // Calculate the minimum and maximum dispatch days based on the values provided
  const minDispatchDate = startDay.add(Number(minDispatch), 'day');
  const maxDispatchDate = startDay.add(Number(maxDispatch), 'day');

  if (shipmentStatus === 'ordered' && currentDate.isAfter(maxDispatchDate)) {
    // Handle the case where the order has not yet been shipped
    return `Your order has been received. We are working with the vendor to dispatch it as soon as possible.`;
  }

  if (minDispatch === maxDispatch) {
    // Check if the minimum and maximum dispatch days are the same
    const dispatchDay = currentDate.isAfter(minDispatchDate)
      ? currentDate
      : minDispatchDate;

    return `Your order has been received and will be dispatched on ${dispatchDay.format(
      'dddd Do MMM',
    )}`;
  }
  // Create string for range of days
  const dispatchDay =
    currentDate.isAfter(minDispatchDate) &&
    currentDate.isBefore(maxDispatchDate)
      ? currentDate
      : minDispatchDate;

  return `Your order has been received and will be dispatched between ${dispatchDay.format(
    'dddd Do MMM',
  )} and ${maxDispatchDate.format('dddd Do MMM')}`;
}

export default function TrackingDetails({
  shipment,
  shipmentStatus,
  storeTimeZone,
}: {
  shipment: Shipment;
  shipmentStatus: string;
  storeTimeZone: string;
}) {
  const isFulfilled = shipment.lineItems?.some(
    (lineItem) => lineItem.isFulfilled,
  );

  if (
    !isFulfilled &&
    shipment.lineItems[0].properties.filter(
      (prop) => prop.name === '_add_cart_date_time',
    ).length
  ) {
    const dispatchDays = generateDispatchDays(shipment, shipmentStatus);

    return (
      <p className="w-[90%] justify-self-center p-4 text-sm sm:mx-auto sm:p-0 sm:text-[11px]">
        {dispatchDays}
      </p>
    );
  }

  const courierTrackingUrl = shipment?.courierTrackingUrl
    ? new URL(shipment?.courierTrackingUrl)
    : null;

  const contactCourierTrackingHost = courierTrackingUrl?.host;

  const courierTrackingBaseUrl = courierTrackingUrl
    ? `${courierTrackingUrl?.protocol}//${courierTrackingUrl?.host}`
    : '#';

  const contactCourierUrl = settings.shipping_company_contact_urls?.find(
    (contactUrls) => contactUrls.includes(contactCourierTrackingHost),
  );

  // need to deep copy before it can be reversed
  const checkpoints = [...(shipment.tracking?.checkpoints ?? [])];

  checkpoints.reverse();
  const showedCheckpoints = checkpoints.slice(0, 3);
  const remainingCheckpoints = checkpoints.slice(3, checkpoints.length);

  //  Need redo this part (Shipping Events) later on the overall structure is incorrect - Bay
  return (
    <div className="h-full w-full flex-wrap px-4 py-5 text-sm">
      <div className="mb-4 flex flex-wrap border-b border-gray-200 pb-4">
        <div className="flex w-1/2 flex-wrap sm:w-[20%]">
          <p className="w-full whitespace-nowrap text-gray-400 sm:whitespace-normal">
            Shipped by
          </p>
          <p className="w-full whitespace-nowrap sm:whitespace-normal">
            {shipment.shippedBy || '-'}
          </p>
        </div>
        <div className="flex w-1/2 flex-wrap sm:ml-0 sm:w-[30%]">
          <div className="w-full pl-0 sm:pl-2">
            <p className="w-full text-gray-400">Tracking ID</p>
            <a
              href={shipment.courierTrackingUrl || '#'}
              target="_blank"
              className="flex w-full justify-start font-bold"
            >
              <span
                // eslint-disable-next-line tailwindcss/no-custom-classname
                className="custom-scrollbar-thin-light overflow-x-auto whitespace-nowrap"
              >
                {shipment.tracking?.tracking_number || '-'}{' '}
              </span>
              {shipment.courierTrackingUrl && (
                <div className="ml-1 mt-[2px] flex h-[14px] w-[14px] items-center justify-center sm:mt-1">
                  <Image alt="tick" src={openLink} />
                </div>
              )}
            </a>
          </div>
        </div>
        <div className="flex w-[100%] flex-wrap pt-5 sm:w-[50%] sm:pt-0">
          <div className="pl-0 sm:pl-2">
            <p className="w-full text-gray-400">An issue with your shipment?</p>
            <a
              href={contactCourierUrl ?? courierTrackingBaseUrl}
              target="_blank"
              className="hidden w-full font-bold sm:flex"
            >
              Contact courier{' '}
              <div className="ml-1 mt-1 flex h-[14px] w-[14px] items-center justify-center">
                <Image alt="tick" src={openLink} />
              </div>
            </a>
          </div>
          <div className="block w-full font-bold sm:hidden">
            <div className="block sm:hidden">
              <CtaLink
                href={contactCourierUrl ?? courierTrackingBaseUrl}
                variant="outlined"
                target="_blank"
                download
                className="block w-full p-3 font-bold"
              >
                {`Contact Courier`}
              </CtaLink>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col pb-4">
        {checkpoints.length > 0 && (
          <div className="w-full pb-3 font-bold sm:hidden">
            Latest tracking status
          </div>
        )}
        {showedCheckpoints?.map((checkpoint, index) => (
          <TrackingLine
            key={index}
            isLatest={index === 0}
            date={
              checkpoint.checkpoint_time
                ? formatDayJsDateShort(
                    checkpoint.checkpoint_time,
                    storeTimeZone,
                  )
                : '-'
            }
            message={checkpoint.message || ''}
            location={checkpoint.location || '-'}
          />
        ))}
        {remainingCheckpoints.length > 0 && (
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Panel>
                  {remainingCheckpoints?.map((checkpoint, idx) => (
                    <TrackingLine
                      key={idx}
                      isLatest={false}
                      date={
                        checkpoint.checkpoint_time
                          ? formatDayJsDateShort(
                              checkpoint.checkpoint_time,
                              storeTimeZone,
                            )
                          : '-'
                      }
                      message={checkpoint.message || ''}
                      location={checkpoint.location || '-'}
                    />
                  ))}
                </Disclosure.Panel>
                <Disclosure.Button className="mx-auto -mt-6 flex w-2/5 min-w-[240px] items-center justify-center bg-white text-[#626565]">
                  {open
                    ? `Hide previous shipping events`
                    : `Show previous shipping events`}
                  <div
                    className={cn('relative ml-2 inline-block w-[13px]', {
                      'rotate-180': open,
                    })}
                  >
                    <Image
                      alt="tick"
                      src={chevDown}
                      width={8}
                      height={6}
                      className="w-full object-contain"
                    />
                  </div>
                </Disclosure.Button>
              </>
            )}
          </Disclosure>
        )}
      </div>
    </div>
  );
}
